/**
 * Client side of `GET /api/waadoo-platform/app-types` and
 * `GET /api/waadoo-platform/app-types/<name>`
 * (plugins/waadoo-core-backend/src/plugins/waadooPlatform/appTypesRoute.ts): the
 * application types of the platform, from the catalog's `AppType` entities.
 *
 * Read for the templates a project's type designates (`templateRoles.ts`,
 * step 7 of the platform plan); the screens driven by the rest of the
 * descriptor are step 8. Here because the descriptor is no one screen's own.
 *
 * The types are re-declared rather than imported, for the reason given in
 * `projectEnvironments/pipelines.ts`: they are the contract of the route, to be
 * changed on both sides at once. `spec` is left as the backend answers it:
 * the catalog validated it against `apptype.schema.json` at ingestion.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

/** One application type of the list (backend `AppTypeSummary`). */
export interface AppTypeSummary {
  name: string;
  title: string;
  description?: string;
  annotations: Record<string, string>;
}

/** One application type with its descriptor (backend `AppTypeDetail`). */
export interface AppTypeDetail extends AppTypeSummary {
  spec: Record<string, unknown>;
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

const PLUGIN_ID = 'waadoo-platform';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isText = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

/** The summary of `value`; undefined without a name or a title. */
function readSummary(value: unknown): AppTypeSummary | undefined {
  if (!isRecord(value) || !isText(value.name) || !isText(value.title)) {
    return undefined;
  }
  const annotations: Record<string, string> = {};
  if (isRecord(value.annotations)) {
    for (const [key, text] of Object.entries(value.annotations)) {
      if (typeof text === 'string') annotations[key] = text;
    }
  }
  return {
    name: value.name,
    title: value.title,
    ...(isText(value.description) ? { description: value.description } : {}),
    annotations,
  };
}

/** The list route's answer. Throws when it has no `items` list. */
export function readAppTypes(payload: unknown): AppTypeSummary[] {
  if (!isRecord(payload) || !Array.isArray(payload.items)) {
    throw new Error('AppTypes API: unexpected answer');
  }
  return payload.items
    .map(readSummary)
    .filter((item): item is AppTypeSummary => item !== undefined);
}

/** One type's answer. Throws without a name, a title or a `spec` object. */
export function readAppType(payload: unknown): AppTypeDetail {
  const summary = readSummary(payload);
  if (!summary || !isRecord(payload) || !isRecord(payload.spec)) {
    throw new Error('AppTypes API: unexpected answer');
  }
  return { ...summary, spec: payload.spec };
}

/** Every application type, by title. Rejects on a failed request. */
export async function fetchAppTypes(apis: Apis): Promise<AppTypeSummary[]> {
  // Absolute backend URL: in development the frontend runs on another port.
  const base = await apis.discoveryApi.getBaseUrl(PLUGIN_ID);
  const response = await apis.fetchApi.fetch(`${base}/app-types`);
  if (!response.ok) {
    throw new Error(`AppTypes API: HTTP ${response.status}`);
  }
  return readAppTypes(await response.json());
}

/**
 * The application type `name`; undefined on a 404 (no such type). Rejects
 * on any other failure.
 */
export async function fetchAppType(
  apis: Apis & { name: string },
): Promise<AppTypeDetail | undefined> {
  const base = await apis.discoveryApi.getBaseUrl(PLUGIN_ID);
  const response = await apis.fetchApi.fetch(
    `${base}/app-types/${encodeURIComponent(apis.name)}`,
  );
  if (response.status === 404) {
    return undefined;
  }
  if (!response.ok) {
    throw new Error(`AppTypes API: HTTP ${response.status}`);
  }
  return readAppType(await response.json());
}

export type AppTypesState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; items: AppTypeSummary[] };

export type AppTypeState =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed' }
  | { status: 'ready'; appType: AppTypeDetail };

/** The application types, read once per mount. */
export function useAppTypes(): AppTypesState {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [state, setState] = useState<AppTypesState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const items = await fetchAppTypes({ fetchApi, discoveryApi });
        if (active) setState({ status: 'ready', items });
      } catch {
        if (active) setState({ status: 'failed' });
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi]);

  return state;
}

/**
 * The application type `name`, read again when `name` changes — `loading`
 * meanwhile, never the previous type's answer.
 */
export function useAppType(name: string): AppTypeState {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [state, setState] = useState<{ name: string; value: AppTypeState }>();

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const appType = await fetchAppType({ fetchApi, discoveryApi, name });
        if (active) {
          setState({
            name,
            value: appType
              ? { status: 'ready', appType }
              : { status: 'missing' },
          });
        }
      } catch {
        if (active) setState({ name, value: { status: 'failed' } });
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, name]);

  return state && state.name === name ? state.value : { status: 'loading' };
}
